// AC GUIDE 2: HOW MANY UNITS DOES A 1.5 TON AC USE? (Oct 2026)
// /blog/ac-power-consumption-units-per-hour
//
// Rebuilt 20 Oct 2026 to GUIDES_PLAYBOOK.md section 3. The answer is real
// use, not just the label: on its star label a 1.5 ton AC uses about half a
// unit an hour, an average; in a bedroom at night less; on a top floor, in a
// hall or with an AC too small for the room it runs at full power and uses
// two to three times that (Saheb's point: an AC saves and costs as expected
// only when it can reach the set temperature).
//
// Same rules as the other guides: every fact from Daikin, the Government of
// India or Citi Cool; each claim with a source key; the page
// (pages/UnitsGuide.jsx) and the copy Google reads (scripts/guide-pages.js)
// both build from unitsContent(). No imports. **double stars** mean bold.
//
// THE NUMBERS. Label units an hour = units a year on the BEE label ÷ 1,600.
// Full power = 230 V × Daikin's rated operating current (data/acSpecs.js).
// Rooms: the room model (data/roomDay.js), run by the caller.

export const UNITS_SLUG = 'blog/ac-power-consumption-units-per-hour';
export const UNITS_PUBLISHED = '2026-10-17';
export const UNITS_UPDATED = '2026-10-20';

export const UNITS_SEO = {
  title: '1.5 Ton AC Power Consumption: Units per Hour, Day and Month | Citi Cool',
  meta: 'How many units does a 1.5 ton AC use? About half a unit an hour on its star label. On a top floor or in a hall, two to three times that. Check your bill.',
  eyebrow: 'AC Guide · Running cost',
  h1: 'How many units does a 1.5 ton AC use in an hour?',
  crumb: 'AC units per hour',
  readMins: 4,
};

export const RATE = 8;            // ₹ a unit for homes
export const DAYS = 30;           // a month counts as 30 days
export const LABEL_HOURS = 1600;  // BEE rates every AC over a 1,600-hour year
export const SIZE_ROWS = ['1.0', '1.5', '1.8', '2.0'];
export const TOOL_SIZES = ['1.0', '1.5', '2.0'];
export const TOOL_HOURS = [4, 8, 12];

// The two rooms of the animation: the same 1.5 ton AC (the 3 Star from
// roomACs()) in each, run through the room model by the caller.
export const TWO_METERS = [
  { key: 'bed', label: 'Bedroom at night', note: 'Middle floor, 10 pm to 6 am',
    room: { city: 'delhi', kind: 'bedroom', size: '12x12', floor: 'mid', sun: 'W', setC: 24, day: 'typical', schedule: 'night' } },
  { key: 'top', label: 'Top floor, hot afternoon', note: 'Top floor, 12 to 6 pm, hottest day',
    room: { city: 'delhi', kind: 'bedroom', size: '12x12', floor: 'top', sun: 'W', setC: 24, day: 'hottest', schedule: 'afternoon' } },
];

// BEE's national bin-hour table: hours a year at each outside temperature,
// 24°C to 43°C, 1,600 in all. BEE Schedule 24, Table 1.
const BEE_BINS = [146, 163, 177, 183, 167, 150, 125, 104, 86, 67, 54, 46, 36, 28, 22, 16, 12, 9, 6, 3];
export const MILD_SHARE = BEE_BINS.slice(0, 7).reduce((a, b) => a + b, 0) / LABEL_HOURS;   // 24 to 30°C

// ─── sources ──────────────────────────────────────────────────────────────

export const UNITS_SOURCES = [
  { key: 'daikin-label', label: 'BEE star label figures from the Daikin RA Master Catalogue 2026, shown on each product page', by: 'Daikin', href: '/daikin-ac-price-list-delhi' },
  { key: 'bee-iseer', label: 'Indian Seasonal Energy Efficiency Ratio: how BEE rates an AC over 1,600 hours a year', by: 'Vikaspedia, Government of India', href: 'https://en.vikaspedia.in/viewcontent/energy/policy-support/energy-efficiency/indian-seasonal-energy-efficiency-ratio' },
  { key: 'bee-schedule', label: 'Star labelling schedule: the ISEER method and the national bin-hour table', by: 'Bureau of Energy Efficiency', href: 'https://beeindia.gov.in/sites/default/files/Schedule_24_LCAC.pdf' },
  { key: 'daikin-specs', label: "Each model's page: rated operating current (GTQ50 5.8 A; GTKL50 and GTKC50 7.17 A; FTKU50 5.51 A)", by: 'Daikin India', href: 'https://daikinindia.com/product-category/splitac' },
  { key: 'calc', label: "Our room model: Daikin's method for how much heat gets into a room, hour by hour, on IMD weather for Delhi, as in our AC calculator", by: 'Citi Cool engineering', href: '/ac-calculator' },
  { key: 'lok-sabha', label: 'Lok Sabha Unstarred Question 2613 on energy efficiency, 27 December 2018', by: 'Ministry of Power', href: 'https://eparlib.nic.in/bitstream/123456789/803193/1/AU2613.pdf' },
  { key: 'pib-faq', label: 'Frequently Asked Questions on BEE recommendations on temperature setting of air conditioners, 29 June 2018', by: 'PIB, Ministry of Power', href: 'https://www.pib.gov.in/newsite/PrintRelease.aspx?relid=180281&reg=48&lang=2' },
  { key: 'daikin-care', label: 'Product Care: clean the air filters every two weeks, close windows and doors, draw the curtains', by: 'Daikin India', href: 'https://www.daikinindia.com/node/594' },
  { key: 'derc', label: 'Electricity tariff schedule, domestic energy charges by slab (FY 2019-20)', by: 'Delhi Electricity Regulatory Commission', href: 'https://www.derc.gov.in/sites/default/files/TARIFF%20SCHEDULE%20FY%202019-20.pdf' },
];

// ─── arithmetic ───────────────────────────────────────────────────────────

export const inrU = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
export const inr100 = (n) => `₹${(Math.round(n / 100) * 100).toLocaleString('en-IN')}`;
const round10 = (n) => Math.round(n / 10) * 10;
export const sizeLabel = (s) => `${Number(s)} ton`;
export const perHour = (model) => model.annualKwh / LABEL_HOURS;

// Every split AC the shop lists (not the Hot & Cold), lowest and highest
// label units an hour for one size.
export function sizeSpan(catalogue, prices, size) {
  const kitOf = (m) => (m.variants.withoutKit ? 'withoutKit' : 'withKit');
  const hs = catalogue
    .filter((m) => m.soldAs === size && m.series !== 'FTHT')
    .filter((m) => { const p = (prices[m.id] || {})[kitOf(m)]; return typeof p === 'number' && p > 0; })
    .map(perHour);
  if (!hs.length) return null;
  return { size, lo: Math.min(...hs), hi: Math.max(...hs) };
}

// "₹800 to ₹1,200", or one figure when both round the same.
const range = (a, b, f) => (f(a) === f(b) ? f(a) : `${f(a)} to ${f(b)}`);

// The monthly bill for the tool: size and hours a day -> a sentence.
export function billText(s, hours) {
  const lo = s.lo * hours * DAYS * RATE; const hi = s.hi * hours * DAYS * RATE;
  return `A ${sizeLabel(s.size)} AC at ${hours} hours a day costs about **${range(lo, hi, inr100)} a month** on its star label.`;
}

// The two meters: cmps from roomComparison() for TWO_METERS, each run with [ac].
export function twoMeters(cmps, ac) {
  return TWO_METERS.map((m, i) => {
    const r = cmps[i].rows[0];
    const hours = cmps[i].hours.length;
    return { ...m, code: ac.code, hours, units: r.units, perHour: r.units / hours, flat: r.flatHours };
  });
}

// ─── the page ─────────────────────────────────────────────────────────────

// spans: sizeSpan() for SIZE_ROWS (nulls dropped); g: pickGuideModels() for
// 1.5 ton; specs: AC_SPECS; meters: twoMeters().
export function unitsContent(spans, g, specs, meters) {
  const s15 = spans.find((s) => s.size === '1.5');
  const month = (h) => h * 8 * DAYS;
  const fulls = g.list.filter((x) => specs[x.model.id]).map((x) => (specs[x.model.id].amps * 230) / 1000);
  const bed = meters[0]; const top = meters[1];
  const times = Math.round(top.perHour / bed.perHour);
  const label = top ? g.list.find((x) => x.code === top.code) : null;
  const vsLabel = label ? Math.round((top.perHour / perHour(label.model)) * 10) / 10 : null;
  const s1 = spans.find((s) => s.size === '1.0');
  const F = g.fixed; const V = g.five;

  return {
    answer: {
      text: '**Expect about half a unit an hour from a 1.5 ton AC.** That is its star-label average. In a bedroom at night it uses less. On a top floor or in a hall, expect two to three times as much. The same goes for an AC too small for the room.',
      src: ['daikin-label', 'calc'],
    },
    because: [
      { text: 'The star label is an average. Most of its hours are mild, not a Delhi afternoon.', src: ['bee-iseer', 'bee-schedule'] },
      ...(fulls.length ? [{ text: `At full power, it uses up to about ${Math.round(Math.max(...fulls) * 10) / 10 >= 1.5 ? 'one and a half' : 'one and a quarter'} units an hour.`, src: ['daikin-specs'] }] : []),
      { text: `At 8 hours a day, the label average costs about ${range(month(s15.lo) * RATE, month(s15.hi) * RATE, inr100)} a month.`, src: ['daikin-label'] },
    ],
    watch: {
      title: 'Watch: one AC\'s electricity meter in two rooms',
      captions: [
        { at: 0, text: 'The same 1.5 ton AC in two rooms. Each room has its own meter.' },
        { at: 0.25, text: 'The bedroom at night cools fast. The AC slows down. Its meter turns slowly.' },
        { at: 0.5, text: 'On the top floor, the sun heats the roof. The AC runs at full power all afternoon.' },
        { at: 0.8, text: `The top-floor meter spins about ${times} times faster.${vsLabel && vsLabel >= 2 ? ` It uses over ${Math.floor(vsLabel)} times the label average.` : ''}` },
      ],
      note: `The Daikin ${bed.code}, set to 24°C, in a 12 × 12 ft room in Delhi.`,
      noteSrc: ['calc', 'daikin-label'],
    },
    table: {
      title: 'How many units does an AC use in a month?',
      head: ['AC size', 'Units a month', 'Bill a month'],
      rows: spans.map((s) => [
        sizeLabel(s.size),
        range(month(s.lo), month(s.hi), (v) => String(round10(v))),
        range(month(s.lo) * RATE, month(s.hi) * RATE, inr100),
      ]),
      note: `At 8 hours a day and ${inrU(RATE)} a unit, from each Daikin AC's star label. A top floor or a hall can use two to three times this.`,
      src: ['daikin-label', 'calc'],
    },
    sides: [
      {
        key: 'save',
        title: 'How to use fewer units: do\'s and don\'ts',
        goodHead: 'Do',
        badHead: 'Don\'t',
        good: [
          { text: 'Set [24°C or warmer](/blog/ideal-ac-temperature). Each degree warmer saves about 6%.', src: ['lok-sabha'] },
          { text: 'Clean the air filters every two weeks.', src: ['daikin-care'] },
          { text: 'Close doors and windows. Draw the curtains.', src: ['daikin-care'] },
          { text: 'Buy the [right size](/blog/what-size-ac-do-i-need) for your room. Our [AC calculator](/ac-calculator) works it out.', src: ['calc'] },
        ],
        bad: [
          { text: 'Don\'t set 18°C to cool faster. It only costs more.', src: ['pib-faq'] },
          { text: 'Don\'t trust the label alone for a top-floor room.', src: ['calc'] },
          { text: 'Don\'t use a small AC for a big hall. It stays at full power.', src: ['calc'] },
        ],
      },
    ],
    parts: [
      {
        id: 'label',
        q: 'Why is the star label only an average?',
        bullets: [
          { text: 'The government tests every AC over 1,600 hours of a year.', src: ['bee-iseer'] },
          { text: `About ${Math.round(MILD_SHARE * 10)} in 10 of those hours are 30°C or cooler outside.`, src: ['bee-schedule'] },
          { text: 'A Delhi summer afternoon is much hotter. So the AC uses more then.', src: ['bee-schedule'] },
        ],
      },
      {
        id: 'top-floor',
        q: 'Why does a top-floor room use more units?',
        bullets: [
          { text: 'The sun heats the roof all day. The roof heats the room.', src: ['calc'] },
          { text: 'So the AC runs at full power for hours. It cannot slow down.', src: ['calc'] },
          { text: `In our test, it used about ${times} times as much as a bedroom at night.`, src: ['calc'] },
        ],
      },
      {
        id: 'month',
        q: 'How much does a 1.5 ton AC cost to run a month?',
        bullets: [
          { text: `At 8 hours a day, about ${range(month(s15.lo) * RATE, month(s15.hi) * RATE, inr100)} on the label.`, src: ['daikin-label'] },
          ...(F && V ? [{ text: `A [5 Star](/blog/3-star-vs-5-star-ac) costs the least. The ${V.code} uses ${Math.round((1 - V.model.annualKwh / F.model.annualKwh) * 100)}% less than the ${F.code}.`, src: ['daikin-label'] }] : []),
          { text: 'We use ₹8 a unit. An AC\'s units land in Delhi\'s top slabs.', src: ['derc'] },
        ],
      },
    ],
    faq: [
      {
        q: 'How many units does a 1.5 ton AC use in 1 hour?',
        a: 'About half a unit, on its star label. At full power on a hot afternoon, two to three times that.',
        src: ['daikin-label', 'daikin-specs'],
      },
      {
        q: 'How many units does a 1.5 ton AC use in a day?',
        a: `At 8 hours a day, about ${range(s15.lo * 8, s15.hi * 8, (v) => String(Math.round(v)))} units on the label.`,
        src: ['daikin-label'],
      },
      {
        q: 'How many units does a 1.5 ton AC use in a month?',
        a: `At 8 hours a day, about ${range(month(s15.lo), month(s15.hi), (v) => String(round10(v)))} units. That is about ${range(month(s15.lo) * RATE, month(s15.hi) * RATE, inr100)} at ${inrU(RATE)} a unit.`,
        src: ['daikin-label'],
      },
      ...(s1 ? [{
        q: 'How many units does a 1 ton AC use?',
        a: `At 8 hours a day, about ${range(month(s1.lo), month(s1.hi), (v) => String(round10(v)))} units a month on the label.`,
        src: ['daikin-label'],
      }] : []),
      {
        q: 'Does an inverter AC use fewer units?',
        a: 'Yes, in a room it can cool fully. On a top floor at full power, it saves nothing. See [inverter or non-inverter](/blog/inverter-vs-non-inverter-ac).',
        src: ['daikin-label', 'calc'],
      },
      {
        q: 'Why is my AC bill higher than the label?',
        a: 'Your room may be hotter than the label\'s average. A top floor, west sun or a big hall keeps the AC at full power. Dirty filters add to the bill too.',
        src: ['calc', 'daikin-care'],
      },
    ],
    tool: {
      title: 'Check your monthly AC bill',
      examples: TOOL_SIZES.map((sz) => spans.find((s) => s.size === sz)).filter(Boolean)
        .map((s) => billText(s, 8)),
    },
  };
}
